import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useCallback } from 'react';
import { Table, useLocalizeMessage, Button, useDialog } from 'libs.nucleus';
import { AddEditMeasureModal } from './add_edit_measure_modal.component';
import { CloneMeasureModal } from './clone_measure_modal.component';
import { EmptyMeasuresState } from './empty_measures_state.component';
import { useMeasuresColumns } from './measures_columns.hook';
import { useDataViews } from '../../hooks';
export const MeasuresTab = ({ dataView, onDataViewUpdate, isLoading = false }) => {
    const translate = useLocalizeMessage();
    const dialog = useDialog();
    const { addMeasure, addMeasures, updateMeasure, cloneMeasure, deleteMeasure, isLoading: isApiLoading, } = useDataViews();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMeasureId, setEditingMeasureId] = useState(null);
    const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
    const [cloningMeasureId, setCloningMeasureId] = useState(null);
    const measures = useMemo(() => {
        if (!dataView.data?.measures) {
            return [];
        }
        return Object.entries(dataView.data.measures).map(([key, measure]) => ({
            id: key,
            name: key,
            title: measure.title || key,
            type: measure.type || 'number',
            description: measure.description,
            isVisible: measure.public !== false,
        }));
    }, [dataView.data?.measures]);
    const handleCreateMeasure = useCallback(() => {
        setEditingMeasureId(null);
        setIsModalOpen(true);
    }, []);
    const handleBulkSave = useCallback(async (newMeasures) => {
        const updatedView = await addMeasures(dataView.id, newMeasures, dataView);
        onDataViewUpdate(updatedView);
    }, [dataView, addMeasures, onDataViewUpdate]);
    const handleEditMeasure = useCallback((measureId) => {
        setEditingMeasureId(measureId);
        setIsModalOpen(true);
    }, []);
    const handleDeleteMeasure = useCallback(async (measureId) => {
        const measureName = dataView.data?.measures?.[measureId]?.title || measureId;
        const ok = await dialog.open({
            title: translate('Delete measure'),
            body: translate('Are you sure you want to delete the measure "{measureName}"?', { measureName }),
            actionButtons: [{ isDestructive: true, label: translate('Delete') }, { label: translate('Cancel') }],
        });
        if (ok) {
            try {
                const updatedView = await deleteMeasure(dataView.id, measureId);
                onDataViewUpdate(updatedView);
            }
            catch (error) {
                // Error already handled in hook
            }
        }
    }, [dataView.id, dataView.data?.measures, deleteMeasure, onDataViewUpdate, dialog, translate]);
    const handleCloneMeasure = useCallback((measureId) => {
        setCloningMeasureId(measureId);
        setIsCloneModalOpen(true);
    }, []);
    const handleCloneSave = useCallback(async (name, description) => {
        if (!cloningMeasureId) {
            return;
        }
        try {
            const updatedView = await cloneMeasure(dataView.id, cloningMeasureId, name, description);
            onDataViewUpdate(updatedView);
            setIsCloneModalOpen(false);
            setCloningMeasureId(null);
        }
        catch (error) {
            // Error already handled in hook
        }
    }, [dataView.id, cloningMeasureId, cloneMeasure, onDataViewUpdate]);
    const handleCloseCloneModal = useCallback(() => {
        setIsCloneModalOpen(false);
        setCloningMeasureId(null);
    }, []);
    const handleSaveMeasure = useCallback(async (measure) => {
        try {
            const updatedView = editingMeasureId
                ? await updateMeasure(dataView.id, editingMeasureId, measure, dataView)
                : await addMeasure(dataView.id, measure, dataView);
            onDataViewUpdate(updatedView);
            setIsModalOpen(false);
            setEditingMeasureId(null);
        }
        catch (error) {
            // Error already handled in hook
        }
    }, [dataView, addMeasure, updateMeasure, editingMeasureId, onDataViewUpdate]);
    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setEditingMeasureId(null);
    }, []);
    const isEmpty = !isLoading && !isApiLoading && measures.length === 0;
    // Measures can only be added/edited/deleted when the view has a status of 'Draft' (SSP-3031)
    const isViewMode = dataView.status !== 'DRAFT';
    const isCreateDisabled = isViewMode || isLoading || isApiLoading;
    const isEditDisabled = isViewMode;
    const isDeleteDisabled = isViewMode;
    const isCloneDisabled = isViewMode;
    const measuresColumns = useMeasuresColumns({
        onEdit: handleEditMeasure,
        onDelete: handleDeleteMeasure,
        onClone: handleCloneMeasure,
        isEditDisabled,
        isDeleteDisabled,
        isCloneDisabled,
        isViewMode,
    });
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'mt-6', children: isEmpty ? (_jsx(EmptyMeasuresState, { onCreateMeasure: handleCreateMeasure, disabled: isCreateDisabled })) : (_jsx(Table, { data: measures, columns: measuresColumns, labelNoResults: translate('No measures found'), isLoading: isLoading || isApiLoading, enableSorting: true, useInternalSearch: true, labelSearchField: translate('Search'), customHeaderContent: _jsx(Button, { label: translate('Create measure'), onClick: handleCreateMeasure, variant: 'primary', disabled: isCreateDisabled, className: 'ml-auto' }) })) }), isModalOpen && (_jsx(AddEditMeasureModal, { isOpen: isModalOpen, onClose: handleCloseModal, onSave: handleSaveMeasure, onSaveMany: handleBulkSave, dataView: dataView, isLoading: isApiLoading, isEditMode: !!editingMeasureId, editingMeasureId: editingMeasureId, isReadOnly: isViewMode })), isCloneModalOpen && cloningMeasureId && (_jsx(CloneMeasureModal, { isOpen: isCloneModalOpen, onClose: handleCloseCloneModal, onSave: handleCloneSave, dataView: dataView, sourceMeasureId: cloningMeasureId, isLoading: isApiLoading }))] }));
};
